declare let window: any

import Promise from 'bluebird'
import _ from 'lodash'
import Platform from 'platform'
import queryString from 'query-string'
import apis from 'browser/app/models/apis'
import Cookie from 'js-cookie'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { browserHistory } from 'browser/history'
import { IconName } from '@blueprintjs/icons'

const DEFAULT_WEBAPP_HOST = 'app.withvector.com'

// https://withvector.atlassian.net/browse/VD-18593
// DISCUSS ANY CHANGES WITH ENGINEERING
// higher debounce introduces negligible wait for users, but greatly reduces unnecessary and expensive load on ES
export const DEBOUNCE_TIMEOUT = 600

export function getPostedByName(postedBy) {
  if (postedBy.displayName) {
    return postedBy.displayName
  } else if (!_.isEmpty(postedBy.first_name) && !_.isEmpty(postedBy.last_name)) {
    return `${postedBy.first_name} ${postedBy.last_name}`
  } else if (!_.isEmpty(postedBy.first_name)) {
    return postedBy.first_name
  } else {
    return postedBy.email
  }
}

export type GeolocationError = {
  message?: string
  nativeError?: GeolocationPositionError
}

export function getCurrentGeoPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject: (e: GeolocationError) => void, onCancel) => {
    let isCanceled = false
    const handleSuccess: PositionCallback = (result: GeolocationPosition) => {
      if (isCanceled) {
        return
      }
      const { latitude, longitude } = result.coords
      if (latitude === 0 && longitude === 0) {
        reject({ nativeError: { code: GeolocationPositionError.POSITION_UNAVAILABLE } as GeolocationPositionError })
        return
      }
      resolve(result)
    }
    const handleError: PositionErrorCallback = (nativeError: GeolocationPositionError) => {
      if (!isCanceled) {
        reject({ nativeError })
      }
    }
    if (!navigator.geolocation) {
      reject({ message: 'Location not available' })
    } else {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    }
    onCancel(() => (isCanceled = true))
  })
}

export function getInitialsColor(email) {
  const colors = [
    'red',
    'pink',
    'purple',
    'deepPurple',
    'indigo',
    'blue',
    'lightBlue',
    'cyan',
    'teal',
    'green',
    'lightGreen',
    'amber',
    'orange',
    'deepOrange',
    'brown',
    'blueGrey',
  ]
  const computeHash = (str: string) => {
    let hash = 0
    for (let i = 0; i < str.length; ++i) {
      hash += str.charCodeAt(i)
    }
    return hash
  }
  return colors[computeHash(email) % colors.length]
}

export function getMobileAgent() {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
    return 'ios'
  }
  if (/Android/.test(navigator.userAgent) && !window.MSStream) {
    return 'android'
  }
  return false
}

export function flattenMessages(nestedMessages, prefix = '') {
  return Object.keys(nestedMessages || {}).reduce<Record<string, string>>((messages, key) => {
    const value = nestedMessages[key]
    const prefixedKey = prefix ? `${prefix}.${key}` : key

    if (typeof value === 'string') {
      messages[prefixedKey] = value
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey))
    }

    return messages
  }, {})
}

/**
 * Returns a randomized alphanumeric string
 *
 * @param length - desired length of the random string [default: 6]
 * @returns randomized alphanumeric string of specified length
 */
export function generateRandomAlphaNumericString(length = 6) {
  return Math.random()
    .toString(36)
    .substring(2, length + 2)
    .toUpperCase()
}

export function isMobilePlatform() {
  return Platform.os.family === 'Android' || Platform.os.family === 'iOS'
}

/**
 * If this page is opened on the mobile platform and with defer, treat it like deferrer deeplink
 * if there is a isDeferLink
 *
 * 1. Mobile user opens the page https://app.withvector.com/register?deferLink=true&firstName=Minh
 * 2. This page will detect this is from mobile with deferLink is true
 * 3. If Android, it will create a deferred deeplink with whatever in the url path + query parameters
 *    and encoded it a blob in utm_content
 * 4. If iOS, it will encode the path/parameters inside utm_content as well to make it consistent
 *    as Android, then will use BranchIO to install the app
 * 5. Once the app is installed, it will get the deferred deeplink
 *    a. Android: will get the deferred deeplink from PlayStore, deserialize the blob
 *       from utm_content and treat it the same as deeplink
 *    b. iOS: will get utm_content value from branchIO, deserialize it, and also route it to
 *        same path as the deeplink
 *
 *  The idea to route the deferred deeplink to normal deep link once the app is installed
 */
export function handleMobileDeferredDeepLink(location: Pick<Location, 'pathname' | 'search'>) {
  const { isDeferredLink } = queryString.parse(decodeURIComponent(location.search))

  // Dont want to change the existing behavior and also allow the user to open the webpage normally
  // in mobile.  Only branch out if there is "deferredLink" in the parameter
  if (_.isEmpty(isDeferredLink)) {
    return false
  }

  const passthroughValue = _.get(location, 'pathname', '') + _.get(location, 'search', '')

  if (Platform.os.family === 'Android') {
    // tslint:disable-next-line:max-line-length

    const serializedPassthroughValue = encodeURIComponent(passthroughValue)
    const androidDeferredLink = encodeURIComponent(
      `utm_source=utm_content&utm_content=${serializedPassthroughValue}`
    )
    const googlePlayStoreLink = `https://play.google.com/store/apps/details?id=com.convoy.loaddoc&referrer=${androidDeferredLink}`
    // window.location.replace(googlePlayStoreLink)
    window.location.href = googlePlayStoreLink
    return true
  } else if (Platform.os.family === 'iOS') {
    /*
     * For iOS, unlike Google PlayStore, the App Store will not forward any data to our app after the installation,  branch.io is used instead.
     * The web will ask the server to generate the branch link with the given parameters and open the branch link.
     * This path is basically the same as invite via text message.
     */
    const url = apis.getDynamicLink({ deferredLink: passthroughValue })
    if (!_.isEmpty(url)) {
      window.location.href = url
      return true
    }
  }
  return false
}

export function qrOnboardingCode(firm: unknown) {
  const companyCodes = _.get(firm, 'firm.companyCodes', [])
  const defaultCompanyCodes = _.filter(companyCodes, { isDefault: true })
  const companyCode = _.get(defaultCompanyCodes, '[0].code', '')

  const hostName = _.isEmpty(apis.WEBAPP_URL) ? 'https://app.loaddocs.co' : apis.WEBAPP_URL
  const deepLink = new URL(hostName + '/register')

  deepLink.searchParams.append('companyCode', companyCode)
  deepLink.searchParams.append('isDeferredLink', 'true')
  return deepLink.toString()
}

export function getDOTNumber(firm: unknown) {
  const companyCodes = _.get(firm, 'firm.companyCodes', [])
  let dotCodes = []

  dotCodes = _.filter(companyCodes, { division: 'USDOT' })
  if (_.isEmpty(dotCodes)) {
    dotCodes = _.filter(companyCodes, { isDefault: true })
  }

  return _.get(dotCodes, '[0].code', '')
}

export const getBrowserVersion = () => {
  return navigator.userAgent
}

function getDomains(domain: string) {
  const APP_PREFIX = 'app.'

  if (domain.startsWith(APP_PREFIX)) {
    return [domain, domain.replace(APP_PREFIX, '')]
  }

  return [domain]
}

export function clearCookie(cookie: string) {
  const currentDomains = getDomains(document.location.hostname)

  currentDomains.forEach((domain) => {
    Cookie.remove(cookie, { domain: domain })
  })
}

export function getDebugId(frames: FramesManager): string {
  const valuePath = frames?.getContext('valuePath')
  return _.join(valuePath, '.')
}

export function followLink(link: string = '') {
  if (isVectorLink(link)) {
    const relativeLink = getRelativeLink(link)
    browserHistory.push(relativeLink)
  } else {
    location.href = link
  }
}

export function getRelativeLink(link: string) {
  try {
    const url = new URL(link)
    return url.pathname + url.search + url.hash
  } catch (error) {
    console.error('Unable to parse link', link)
  }
  return ''
}

export function isVectorLink(link: string = '') {
  // on the local environment, WEBAPP_URL is empty because the host is empty.
  // Since link.indexOf('') on an empty string always returns true, set it to undefined if empty
  // to avoid a false positive.
  const internalDomains = [
    DEFAULT_WEBAPP_HOST,
    apis.WEBAPP_URL || undefined,
    apis.MOBILEWEB_URL || undefined,
  ]

  return internalDomains.some((domain) => link.indexOf(domain) !== -1)
}

export function getPlatform(): string {
  return Platform.name
}

export function stringToBoolean(value: any): boolean {
  const trueValues = ['true', 'yes', 'y', '1', 't', 'on']
  if (_.isNil(value)) {
    return false
  }
  return (
    (typeof value === 'boolean' && value === true) ||
    trueValues.indexOf(value.toString()?.toLowerCase()) !== -1
  )
}

function getUISchema(frames: FramesManager | undefined) {
  if (!frames) {
    return
  }

  const path = frames.getContext('uiSchemaPath')

  return path ? path.join('.') : undefined
}

export function creationSourceFromWeb(location, frames = undefined) {
  const uiSchema = getUISchema(frames)

  return {
    platform: apis?.getSettings().getPlatform(),
    url: window.location.pathname,
    location,
    uiSchema,
  }
}

/**
 * Detects if the browser is running in a headless or automated environment.
 * @returns {boolean} True if an automated environment or headless
 */
export function isAutomatedOrHeadless() {
  return isHeadless() || isAutomated()
}

/**
 * Detects if the browser is running in a headless mode.
 * @returns {boolean} True if a headless browser is detected.
 */
export function isHeadless() {
  try {
    // 1. navigator.webdriver is true in most headless Chrome/Edge/Firefox sessions
    if (navigator.webdriver) {
      return true
    }

    // 2. User-Agent check for common headless patterns
    const ua = window.navigator.userAgent
    return  (/HeadlessChrome|headless/i.test(ua))

  } catch (e) {
    console.warn('Error checking for headless mode:', e)
    return false
  }
}

/**
 * Detects if the browser is running in an automated environment
 * @returns {boolean} True if an automated environment is detected.
 */
export function isAutomated() {
  try {
    // Known automation globals
    if (
      window.Cypress ||
      window._phantom ||
      window.phantom ||
      window.callPhantom ||
      window.__nightmare
    ) {
      console.warn('Detected automation global in window')
      return true
    }

    //  Puppeteer / Selenium markers
    if (window.document.$cdc_asdjflasutopfhvcZLmcfl_ || window.document.$chrome_asyncScriptInfo) {
      console.warn('Detected puppeteer/selenium marker')
      return true
    }

    // Not automated
    return false
  } catch (e) {
    console.warn('Error checking for headless mode:', e)
    return false
  }
}

/**
 * Fixes autocompletion on Blueprint's IconName by redefining their `MaybeElement`, which degrades
 * to `any` due to `React.JSX` not existing in React 16. This uses JSX.Element instead. In React 19,
 * the global JSX goes away in favor of React.JSX (introduced in 18?). When we upgrade React, we can
 * simplify back to MaybeElement.
 */
export type BlueprintIcon = IconName | JSX.Element | false | null | undefined

/**
 * Copies the provided text to the user's clipboard.
 * Uses the modern Clipboard API with a fallback to execCommand.
 * * @param text - The string to be copied to the clipboard.
 */
export async function copyToClipboard(text: string) {
  // 1. Try the modern Navigator Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch (err) {
      console.log('Clipboard API failed, falling back...', err)
    }
  }

  // 2. Fallback: Create a hidden textarea and use execCommand
  const textArea = document.createElement('textarea')
  textArea.value = text

  // Ensure the textarea is not visible but remains part of the DOM
  textArea.style.position = 'fixed'
  textArea.style.left = '-9999px'
  textArea.style.top = '0'
  document.body.appendChild(textArea)

  textArea.focus()
  textArea.select()

  try {
    const successful = document.execCommand('copy')
    if (!successful) {
      console.log('Copy command was unsuccessful')
    }
  } catch (err) {
    console.log('Fallback copy failed:', err)
  } finally {
    // Clean up: remove the temporary element
    document.body.removeChild(textArea)
  }
}

export function unrefString(value) {
  if (typeof value !== 'string') {
    return value
  }

  const obj: Record<string, undefined> = {}
  obj[value] = undefined // V8 flattens strings used as object keys
  return Object.keys(obj)[0]
}
